import { formatKnownNeo4jType } from '@/helpers/graph.helpers';

/**
 * One place that turns a value which is not a primitive into readable text.
 *
 * Every surface that renders a cell used to end in a bare `String(value)`.
 * That is correct for strings, numbers and booleans and useless for anything
 * else: a nested map or list becomes the literal `[object Object]`, which tells
 * the reader neither what the value is nor that anything is missing. Neo4j's own
 * shapes (Integer, Date, Point, Node, Path…) are handled earlier by
 * `formatNeo4jSpecialTypes`; what reaches here is a plain object or array that
 * came back from a query, a pipeline or a stored property.
 *
 * The compact form is deliberately not JSON. `{"status":"Open","amount":12}` is
 * accurate and reads as machinery; `{status: Open, amount: 12}` fits a table row
 * and says the same thing. The full JSON stays one click away — see
 * `fullNestedValueText` — for the cases where the compact form had to cut.
 */

/** Entries shown before a compact object or array says how many it dropped. */
const COMPACT_ENTRY_LIMIT = 3;

/** How far in the compact form descends before collapsing to `{…}` / `[…]`. */
const COMPACT_DEPTH_LIMIT = 1;

/**
 * Extra levels the expanded view walks for a value that cannot be serialized.
 *
 * `compactAtDepth` counts down from its starting depth, so a negative start buys
 * more levels before the `{…}` collapse.
 */
const CYCLE_EXPANSION_DEPTH = 3;

/** A hard ceiling, so one pathological value cannot fill a column. */
const COMPACT_LENGTH_LIMIT = 120;

const isPlainish = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Whether a value has structure the compact form can only summarize.
 *
 * The question a caller is really asking is "should I offer a way to see the
 * whole thing", so a `Date` — which formats losslessly — is not inspectable
 * even though `typeof` calls it an object.
 */
export function isInspectableValue(value: unknown): boolean {
  return inspectableFullText(value) !== null;
}

/**
 * The full text worth expanding to, or `null` when there is nothing more to
 * show than the cell already shows.
 *
 * One call instead of asking `isInspectableValue` and then re-serializing: a
 * cell body runs on every table render, and the two questions need the same two
 * strings. An empty object or list — and a `Map` or `Set`, which have no own
 * enumerable keys — compacts to exactly what the full view would print, so the
 * button opened a panel repeating the cell verbatim.
 */
export function inspectableFullText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return null;
  if (typeof value !== 'object') return null;
  const full = fullNestedValueText(value);
  return compactNestedValue(value) === full ? null : full;
}

const compactAtDepth = (value: unknown, depth: number): string => {
  if (value === null) return 'null';
  if (value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    if (depth > COMPACT_DEPTH_LIMIT) return '[…]';
    if (value.length === 0) return '[]';
    const shown = value
      .slice(0, COMPACT_ENTRY_LIMIT)
      .map((entry) => compactAtDepth(entry, depth + 1));
    const hidden = value.length - shown.length;
    return `[${shown.join(', ')}${hidden > 0 ? `, +${hidden} more` : ''}]`;
  }
  if (isPlainish(value)) {
    if (depth > COMPACT_DEPTH_LIMIT) return '{…}';
    const entries = Object.entries(value);
    if (entries.length === 0) return '{}';
    const shown = entries
      .slice(0, COMPACT_ENTRY_LIMIT)
      .map(([key, entry]) => `${key}: ${compactAtDepth(entry, depth + 1)}`);
    const hidden = entries.length - shown.length;
    return `{${shown.join(', ')}${hidden > 0 ? `, +${hidden} more` : ''}}`;
  }
  return String(value);
};

/**
 * A one-line summary of a nested value.
 *
 * Never returns `[object Object]`, and survives a cycle: this walks the value
 * itself rather than reaching for `JSON.stringify`, which throws on a cycle and
 * would take the whole cell with it.
 */
export function compactNestedValue(value: unknown): string {
  const compact = compactAtDepth(value, 0);
  if (compact.length <= COMPACT_LENGTH_LIMIT) return compact;
  return `${compact.slice(0, COMPACT_LENGTH_LIMIT - 1)}…`;
}

/**
 * The whole value, pretty-printed, for a surface that can show more than a row.
 *
 * Falls back to the compact form when the value cannot be serialized — a cycle,
 * or a `BigInt` — rather than surfacing a `TypeError` where a value was
 * expected.
 */
export function fullNestedValueText(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? compactAtDepth(value, 0);
  } catch {
    // A cycle: serializing throws, so walk it instead — deep enough to be worth
    // opening. Returning the compact form here made the expanded view repeat the
    // cell verbatim, which is not an answer to "show me the rest".
    return compactAtDepth(value, -CYCLE_EXPANSION_DEPTH);
  }
}

/**
 * The display fallback: primitives exactly as they were, everything else
 * compacted.
 *
 * Drop-in for the `String(value)` that used to end each formatting chain, so a
 * caller keeps its existing behaviour for every value that already rendered
 * correctly.
 */
export function displayValueText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'object') return String(value);
  // A Neo4j Date, Integer or Point is an object too, and listing its internals
  // (`{year: 2026, month: 8, day: 27}`, `{low: 42, high: 0}`) is not readable —
  // these shapes are exactly what survives JSON over HTTP from the API, so they
  // are the common case on the dashboard and evidence surfaces rather than an
  // edge one.
  return formatKnownNeo4jType(value) ?? compactNestedValue(value);
}
